import { Buffer } from 'buffer';
import { s as smartPickTokenValue } from './processTokenUtils.js';

// biome-ignore lint/style/useNodejsImportProtocol: <explanation>

// @NOTE: when updating this, be sure to also update the lambda:
// https://github.com/immutable/platform-infrastructure/blob/main/terraform/cloudfront/immutable-image-resizer-cache/lambda-src/src-ts/constants.ts#L3-L6
var TOLERATED_IMAGE_SIZES = [24, 32, 48, 64, 72, 128, 256, 370, 450, 512, 640, 720, 860, 1024, 1280, 1440, 1600, 1920, 2048, 2160, 2304, 2560, 2732, 2880, 3072, 3200, 3456, 3840, 4096];
function base64UrlEncodeImageUrl(url) {
  return Buffer.from(url).toString("base64").replace(/\+/g, "-") // Convert '+' to '-'
  .replace(/\//g, "_"); // Convert '/' to '_'
}

function removeDuplicates(numbers) {
  // Use a Set to store unique values
  var uniqueNumbers = new Set(numbers);
  // Convert the Set back to an array
  var resultArray = Array.from(uniqueNumbers);
  return resultArray;
}
function getImageSizesArray(currentSize) {
  var index = TOLERATED_IMAGE_SIZES.indexOf(currentSize);
  var closestLargerSize = null;
  if (index === -1) {
    // If currentSize is not found, find the closest larger size
    var largerSizes = TOLERATED_IMAGE_SIZES.filter(function (size) {
      return size > currentSize;
    });
    if (largerSizes.length > 0) {
      closestLargerSize = largerSizes.reduce(function (prev, curr) {
        return Math.abs(curr - currentSize) < Math.abs(prev - currentSize) ? curr : prev;
      });
    }
  }
  var underSize = index > 0 ? TOLERATED_IMAGE_SIZES[index - 1] : null;
  var matchedSize = closestLargerSize !== null ? closestLargerSize : currentSize;
  var largeSize = TOLERATED_IMAGE_SIZES.find(function (size) {
    return size > currentSize;
  });
  var xLargeSize = TOLERATED_IMAGE_SIZES.find(function (size) {
    return size > largeSize;
  });
  var sizes = [underSize, matchedSize, largeSize, xLargeSize];
  var refinedSizes = sizes.filter(function (size) {
    return size !== null;
  });
  var sortedSizes = refinedSizes.sort(function (n1, n2) {
    return n1 - n2;
  });
  var dedupedSizes = removeDuplicates(sortedSizes);
  return dedupedSizes;
}
function pickResponsiveSize(size, sizeMapping) {
  switch (size) {
    case "xLarge":
      return getImageSizesArray(sizeMapping.xLarge);
    case "large":
      return getImageSizesArray(sizeMapping.large);
    case "small":
      return getImageSizesArray(sizeMapping.small);
    case "medium":
    default:
      return getImageSizesArray(sizeMapping.medium);
  }
}
function pickSizeInLayout(size, sizeMapping) {
  switch (size) {
    case "xLarge":
      return "".concat(sizeMapping.xLarge, "px");
    case "large":
      return "".concat(sizeMapping.large, "px");
    case "small":
      return "".concat(sizeMapping.small, "px");
    case "medium":
    default:
      return "".concat(sizeMapping.medium, "px");
  }
}
function getImageSizeMapping(componentKind, theme, asNumbers) {
  var base = theme.base;
  var sizeMapping;
  switch (componentKind) {
    case "SwapStack":
      sizeMapping = {
        small: asNumbers ? Number.parseInt(base.icon.size[200], 10) : base.icon.size[200],
        medium: asNumbers ? Number.parseInt(base.icon.size[250], 10) : base.icon.size[250],
        large: asNumbers ? Number.parseInt(base.icon.size[400], 10) : base.icon.size[400],
        xLarge: asNumbers ? Number.parseInt(base.icon.size[500], 10) : base.icon.size[500]
      };
      break;
    case "Avatar":
    case "FramedStack":
    default:
      sizeMapping = {
        small: asNumbers ? Number.parseInt(base.icon.size[300], 10) : base.icon.size[300],
        medium: asNumbers ? Number.parseInt(base.icon.size[400], 10) : base.icon.size[400],
        large: asNumbers ? Number.parseInt(base.icon.size[500], 10) : base.icon.size[500],
        xLarge: asNumbers ? Number.parseInt(base.icon.size[600], 10) : base.icon.size[600]
      };
      break;
  }
  return sizeMapping;
}
function isImageResizerProps(use, imageProps) {
  return !use;
}
function getMediaRule(rule, breakpoint, width) {
  return width !== "" ? "(".concat(rule === "minWidth" ? "min-width" : "max-width", ": ").concat(breakpoint, ") ").concat(width) : "";
}
function pickBreakpointValue(breakpoint, theme) {
  return "".concat(smartPickTokenValue(theme, "base.breakpoint.".concat(breakpoint)), "px");
}
function replaceDesignTokenValues(width, theme) {
  var pickedValue = width.match(/^base./) ? smartPickTokenValue(theme, width) : width;
  return pickedValue;
}
function convertEntriesToLonghand(entries) {
  return entries.map(function (entry) {
    if ("r" in entry) {
      return entry.r === "default" ? {
        rule: entry.r,
        width: entry.w
      } : {
        rule: entry.r,
        breakpoint: entry.b,
        width: entry.w
      };
    }
    return entry;
  });
}
var constructMq = function constructMq(processedMqRules, defaultWidth) {
  return !processedMqRules.length && defaultWidth ? defaultWidth : "".concat(processedMqRules.length ? processedMqRules.join(",\n") : "").concat(defaultWidth && ", ", " \n").concat(defaultWidth);
};
function mediaQuerySizesHelper(entries) {
  return function (theme) {
    var longhandEntries = convertEntriesToLonghand(entries);
    var defaultRule = longhandEntries.find(function (entry) {
      return entry.rule === "default";
    });
    var processedDefaultRule = defaultRule ? {
      rule: "default",
      width: replaceDesignTokenValues(defaultRule.width, theme)
    } : {
      width: ""
    };
    var processedMqRules = longhandEntries.filter(function (entry) {
      return entry.rule !== "default" && entry.width !== "" && entry.width !== " ";
    }).map(function (entry) {
      var typedEntry = entry;
      var breakpointValue = Object.keys(theme.base.breakpoint).includes(typedEntry.breakpoint) ? pickBreakpointValue(typedEntry.breakpoint, theme) : typedEntry.breakpoint;
      var widthValue = replaceDesignTokenValues(typedEntry.width, theme);
      return getMediaRule(typedEntry.rule, breakpointValue, widthValue);
    });
    return constructMq(processedMqRules, processedDefaultRule.width).trim();
  };
}

export { pickSizeInLayout as a, getImageSizeMapping as b, base64UrlEncodeImageUrl as c, getImageSizesArray as g, isImageResizerProps as i, mediaQuerySizesHelper as m, pickResponsiveSize as p, removeDuplicates as r };
//# sourceMappingURL=imageHelpers2.js.map
